
import { OldPermit, AmmunitionReturn } from "models";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { Permits } from "@/hooks";
import Auth from "@/store/modules/Auth";
import Signature from "@/components/Signature.vue";
import logger from "@/plugins/logger";
import { Device } from "@capacitor/device";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";

@Component({
  components: {
    Signature,
  },
})
export default class ReturnPermit extends Vue {
  @Prop() readonly open!: boolean;
  @Prop() readonly permit!: OldPermit;
  validReturnPermit = false;
  returnAmount: number | null = null;
  remarks = "";
  confirmFirearm = false;
  loading = false;
  returnSignature: string | null = null;
  clearSignature = false;
  rules = {
    required: (value: string): boolean | string =>
      !!value || "This field is Required.",
    negvalcheck: (value: number): boolean | string =>
      value >= 0 || "Can't be less than 0",
  };
  cameraActive = false;
  photoDataUrl: string | null = null;
  photoDateTime: string | null = null;
  photoLocation: string | null = null;
  cameraError: string | null = null;
  mediaStream: MediaStream | null = null;
  geoCoords: { lat: number; lng: number } | null = null;

  @Watch("open")
  modalOpened(): void {
    if (this.open == true) {
      this.returnAmount = this.permit.ammunitionCount;
      this.clearSignature = false;
    }
  }

  close(): void {
    this.validReturnPermit = false;
    this.returnAmount = null;
    this.remarks = "";
    this.confirmFirearm = false;
    this.clearSignature = true;
    this.$emit("close");
    this.stopCamera();
  }
  reload(): void {
    this.$emit("reload");
  }

  get getEnforcedSignature(): boolean {
    if (Auth.getUserOverview) {
      if (
        Auth.getUserOverview.userSignatureID == null ||
        Auth.getUserOverview.userSignatureID == undefined
      ) {
        return true;
      } else {
        return Auth.getUserOverview.securityFirm.enforceSignatures;
      }
    } else {
      return false;
    }
  }

  get getSignaturePreview(): string | null {
    /* `data:${signature.mimeType};base64,${signature.dataFiles}` */
    if (Auth.getUserOverview && Auth.getUserOverview.userSignature) {
      return `data:${Auth.getUserOverview.userSignature.mimeType};base64,${Auth.getUserOverview.userSignature.dataFiles}`;
    } else {
      return null;
    }
  }
  handleSignatureUpdate(b64: string) {
    logger.log("Signature: ", b64);
    if (b64 != undefined) {
      this.returnSignature = b64;
    }
  }
  async submitPermitReturn(): Promise<void> {
    try {
      if (this.returnAmount == null) return Promise.reject("No amount");
      this.loading = true;
      const body: AmmunitionReturn = {
        assignFirearmID: this.permit.id,
        calibre: this.permit.firearm.calibre,
        totalAllocated: this.permit.ammunitionCount,
        returnQuantity: this.returnAmount,
        remarks: this.remarks,
        assignFirearmFiles: [],
      };
      if (this.photoDataUrl) {
        body.assignFirearmFiles.push({
          assignFirearmId: this.permit.id,
          fileBase64: this.photoDataUrl.split(",")[1],
          fileName: `return_photo_${Date.now()}.jpg`,
          fileMimeType: "image/jpeg",
          isIssued: false,
          isReturned: true,
          ipAddress: null,
          geoStamp: this.geoCoords
            ? `${this.geoCoords.lat},${this.geoCoords.lng}`
            : this.photoLocation || "",
          timeStamp: this.photoDateTime!,
        });
      }
      if (this.getEnforcedSignature == true && this.returnSignature != null) {
        body.returnedBySignature = {
          fileName: "",
          fileSize: 0,
          mimeType: "",
          upload: this.returnSignature,
        };
      }
      const res = await Permits.returnPermit(body);
      this.reload();
      this.close();
      // logger.log("Permit returned: ", res);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  async startCamera() {
    this.cameraError = null;

    const deviceInfo = await Device.getInfo();
    if (deviceInfo.platform == "web") {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { exact: "environment" } }, // <-- Use back camera
        });
        this.cameraActive = true;
        this.$nextTick(() => {
          const video = this.$refs.video as HTMLVideoElement;
          if (video && this.mediaStream) {
            video.srcObject = this.mediaStream;
            video.play();
          } else {
            this.cameraError = "Unable to access camera.";
          }
        });

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            this.geoCoords = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            };
            this.photoLocation = `Lat: ${pos.coords.latitude}, Lng: ${pos.coords.longitude}`;
          },
          (err) => {
            this.photoLocation = "Location unavailable";
          }
        );
      } catch (e) {
        // Fallback if environment camera is not available
        try {
          this.mediaStream = await navigator.mediaDevices.getUserMedia({
            video: true,
          });
          this.cameraActive = true;
          this.$nextTick(() => {
            const video = this.$refs.video as HTMLVideoElement;
            if (video && this.mediaStream) {
              video.srcObject = this.mediaStream;
              video.play();
            } else {
              this.cameraError = "Unable to access camera.";
            }
          });
        } catch (err) {
          this.cameraError = "Unable to access camera.";
        }
      }
    } else {
      const perm = await Camera.checkPermissions();
      if (perm.camera != "granted") {
        await Camera.requestPermissions();
      }
      const image = await Camera.getPhoto({
        quality: 100,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        saveToGallery: true,
        source: CameraSource.Camera,
      });
      console.log("IMAGE FROM CAMERA APP: ", image);
      if (image && image.dataUrl) {
        this.photoDataUrl = image.dataUrl;
        this.photoDateTime = new Date().toISOString();
      } else {
        this.$notifCreator.createErrorNotification(
          "Unable to process photo taken. If this problem persists, please contact us."
        );
      }
    }
  }
  stopCamera() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
    this.cameraActive = false;
  }
  capturePhoto() {
    const video = this.$refs.video as HTMLVideoElement;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
    this.photoDataUrl = canvas.toDataURL("image/jpeg");
    this.photoDateTime = new Date().toISOString();
    this.stopCamera();
  }
  removePhoto() {
    this.photoDataUrl = null;
    this.photoDateTime = null;
    this.photoLocation = null;
    this.geoCoords = null;
  }

  formatPhotoDateTime(dt: string | null): string {
    if (!dt) return "";
    // Use browser locale, or customize as needed
    return new Date(dt).toLocaleString();
  }
}
